'use client';

/**
 * FAQSection Component
 *
 * A section that displays frequently asked questions using an accordion component.
 * Desktop: form is always visible on the left, accordion on the right (no click required).
 * Mobile: stacked layout with click-to-open drawer for the ask-query form.
 */
import React from 'react';

import { Accordion, AccordionItem } from '../../components/common/accordion';
import { Button } from '../../components/common/button';
import { Drawer } from '../../components/common/drawer';
import { Section } from '../../components/common/section/Section';
import { AskQueryForm, AskQueryFormValues } from '../../components/features/landing/forms/AskQueryForm';
import { useFaqQuery } from './useFaqQuery';

export interface FAQSectionProps {
  /**
   * Section title
   */
  title?: string;

  /**
   * Optional title to display on mobile views.
   * If not provided, the main 'title' prop will be used.
   */
  mobileTitle?: string;

  /**
   * Array of FAQ items (title and content)
   */
  items: AccordionItem[];

  /**
   * Text for the query prompt
   */
  queryText?: string;

  /**
   * Text for the "See more" link
   */
  seeMoreText?: string;

  /**
   * URL for the "See more" link
   */
  seeMoreLink?: string;

  /**
   * Callback when the query form is submitted.
   * The parent component handles the actual submission logic.
   */
  onSubmit: (data: AskQueryFormValues, attachments?: File[]) => Promise<void>;

  /**
   * Whether the form is currently submitting
   */
  isSubmitting?: boolean;

  /**
   * Translations for the AskQueryForm
   */
  formTranslations?: {
    title?: string;
    email?: string;
    emailPlaceholder?: string;
    question?: string;
    questionPlaceholder?: string;
    attachment?: string;
    attachmentPrompt?: string;
    // Split attachment info into parts
    attachmentInfoPrefix?: string;
    attachmentInfoSuffix?: string;
    attachmentInfoSuffix2?: string;
    // Split max images warning into parts
    maxImagesWarningPrefix?: string;
    maxImagesWarningSuffix?: string;
    submitButton?: string;
    submittingButton?: string;
    responseTime?: string;
    emailError?: string;
    questionError?: string;
    // FAQ section specific translations
    askQueryButton?: string;
    cancelButton?: string;
  };
}

/**
 * FAQSection component for displaying frequently asked questions
 * with an "Ask a Query" functionality
 *
 * @example
 * ```tsx
 * <FAQSection
 *   title="Frequently asked questions"
 *   items={[
 *     { title: 'What is TopTopTime?', content: 'TopTopTime is a platform...' },
 *     { title: 'How do I sign up?', content: 'You can sign up by...' }
 *   ]}
 *   queryText="Got any questions?"
 *   seeMoreText="See more"
 *   seeMoreLink="#all-faqs"
 *   onSubmit={async (data, attachments) => {
 *     // Handle submission in parent
 *   }}
 * />
 * ```
 */
export const FAQSection: React.FC<FAQSectionProps> = ({
  title = 'Frequently asked questions',
  mobileTitle,
  items,
  queryText = 'Got any questions?',
  seeMoreText = 'See more',
  seeMoreLink = '#',
  onSubmit,
  isSubmitting = false,
  formTranslations
}) => {
  const { toggleForm, isMobile } = useFaqQuery();

  // Handle form submission - delegates to parent; on mobile close drawer after submit
  const handleQuerySubmit = async (data: AskQueryFormValues, attachments?: File[]) => {
    await onSubmit(data, attachments);
    if (isMobile) toggleForm(); // Close drawer after submission on mobile
  };

  /**
   * Render the mobile view of the FAQ section
   */
  const renderMobileView = () => {
    const displayTitle = mobileTitle !== undefined ? mobileTitle : title;

    return (
      <div className="space-y-7">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl text-neutral-800">{displayTitle}</h2>
          <div>
            <Button variant="link" label={seeMoreText} onClick={() => (window.location.href = seeMoreLink)} />
          </div>
        </div>

        {/* Mobile Accordion */}
        <Accordion items={items} />

        {/* Mobile query text and button */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-neutral-600">{queryText}</span>
          <Drawer
            trigger={<Button variant="link" label={formTranslations?.askQueryButton || 'Ask a Query'} />}
            side="bottom"
            className="pt-0 pb-2 px-5 rounded-t-xl"
          >
            <AskQueryForm onSubmit={handleQuerySubmit} isLoading={isSubmitting} translations={formTranslations} />
          </Drawer>
        </div>
      </div>
    );
  };

  /**
   * Render the desktop view of the FAQ section.
   * Form is always visible on the left; no click required. No layout flip.
   */
  const renderDesktopView = () => {
    return (
      <div className="flex gap-9">
        {/* Left column: title + form (always visible) */}
        <div className="min-w-[360px] lg:min-w-[444px] flex flex-col gap-5">
          <h2 className="text-2xl text-neutral-800">{title}</h2>
          <div className="min-w-[400px]">
            <AskQueryForm onSubmit={handleQuerySubmit} isLoading={isSubmitting} translations={formTranslations} />
          </div>
        </div>

        {/* Right column: accordion + see more */}
        <div className="w-full flex flex-col border-l border-neutral-200 pl-10">
          <Accordion items={items} />
          <div className="mt-6 text-right">
            <Button variant="link" label={seeMoreText} onClick={() => (window.location.href = seeMoreLink)} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <Section className="py-6 md:py-15" disableVerticalPadding={true} fullWidth={false}>
      {isMobile ? renderMobileView() : renderDesktopView()}
    </Section>
  );
};
